import React, { useCallback, useMemo } from 'react';
import { useIntl } from 'react-intl';
import { TypedMemo } from '../../common/types';
import { generateQRCode } from '../../../utils/qrCode/qrCode';
import {
  downloadQRCodePng,
  downloadQRCodeSvg,
  qrCodePathData,
  qrCodeViewBoxSize,
} from '../../../utils/qrCode/qrCodeSvg';
import { Panel } from '../../music24/components/primitives/Panel';
import styles from './SMLPublishedQRBox.module.scss';

interface SMLPublishedQRBoxProps {
  shareUrl: string;
  /** Used for the downloaded file's name. */
  slug: string;
}

/**
 * The address as a picture, inline.
 *
 * It opens inside the sheet rather than in a second modal: a QR code is
 * something to glance at and download, not a screen to navigate to. The symbol
 * is generated here, so nothing about the page is sent to an image service.
 */
const SMLPublishedQRBoxComponent: React.FC<SMLPublishedQRBoxProps> = ({ shareUrl, slug }) => {
  const intl = useIntl();
  const qr = useMemo(() => generateQRCode(shareUrl), [shareUrl]);
  const filename = slug === '' ? 'smartlink' : slug.replace(/[^a-zA-Z0-9-_]/g, '-');

  const onDownloadPng = useCallback(() => {
    downloadQRCodePng(qr, `${filename}-qr.png`);
  }, [qr, filename]);

  const onDownloadSvg = useCallback(() => {
    downloadQRCodeSvg(qr, `${filename}-qr.svg`);
  }, [qr, filename]);

  const side = qrCodeViewBoxSize(qr);

  return (
    <Panel nested className={styles.box}>
      <svg className={styles.code} viewBox={`0 0 ${side} ${side}`} role="img" aria-label={shareUrl}>
        <rect width={side} height={side} fill="#ffffff" />
        <path d={qrCodePathData(qr)} fill="#000000" />
      </svg>
      <div className={styles.text}>
        <p className={styles.note}>
          {intl.formatMessage({
            id: 'app.sml.published.qr.note',
            defaultMessage: 'For posters, merch tables and the back of a flyer. It points at the same address.',
          })}
        </p>
        <div className={styles.buttons}>
          <button type="button" className={styles.download} onClick={onDownloadPng}>
            {intl.formatMessage({ id: 'app.sml.published.qr.png', defaultMessage: 'Download PNG' })}
          </button>
          <button type="button" className={styles.download} onClick={onDownloadSvg}>
            {intl.formatMessage({ id: 'app.sml.published.qr.svg', defaultMessage: 'Download SVG' })}
          </button>
        </div>
      </div>
    </Panel>
  );
};

export const SMLPublishedQRBox = TypedMemo(SMLPublishedQRBoxComponent);
